import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";

const MAX_POLLS = 12;

export default function PaymentResult({ status }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { refreshSub } = useSubscription();
  const [state, setState] = useState(status === "cancel" ? "canceled" : "verifying");

  const poll = useCallback(async (sessionId, attempt) => {
    if (attempt >= MAX_POLLS) { setState("success"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid" || data.status === "completed") {
        await Promise.all([refreshSub(), refresh()]);
        setState("success");
        return;
      }
      if (data.status === "expired" || data.payment_status === "failed") {
        setState("canceled");
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => poll(sessionId, attempt + 1), 2000);
  }, [refresh, refreshSub]);

  useEffect(() => {
    if (status === "cancel") return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("canceled"); return; }
    poll(sessionId, 0);
  }, [status, poll]);

  const done = state === "success";
  const canceled = state === "canceled";

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
        {state === "verifying" && <Loader2 className="h-14 w-14 text-primary animate-spin mx-auto" />}
        {done && <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto" data-testid="payment-success-icon" />}
        {canceled && <XCircle className="h-16 w-16 text-muted-foreground mx-auto" data-testid="payment-cancel-icon" />}

        <h1 className="mt-6 font-display text-3xl font-extrabold">
          {state === "verifying" ? t("verifying") : done ? t("paymentSuccess") : t("paymentCanceled")}
        </h1>
        {state !== "verifying" && (
          <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
            {done ? t("paymentSuccessSub") : t("paymentCanceledSub")}
          </p>
        )}

        {state !== "verifying" && (
          <Button data-testid="payment-goto-app-btn" onClick={() => navigate("/")} className="mt-8 h-12 px-8 font-semibold">
            {t("goToApp")}
          </Button>
        )}
      </motion.div>
    </div>
  );
}
